import React from "react";
import { motion } from "framer-motion";
import {
  Users,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  TrendingUp,
} from "lucide-react";

const ContactStats = ({ group }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num || 0);
  };

  const isProcessing = !group.uploaded_at && group.number_of_contacts === null;
  const contactCount = group.number_of_contacts || 0;

  const stats = [
    {
      title: "Total Contacts",
      value: isProcessing ? "..." : formatNumber(contactCount),
      icon: Users,
      color: "blue",
      description: isProcessing ? "Processing..." : "Active contacts in group",
    },
    {
      title: "Created",
      value: formatDate(group.created_at),
      icon: Calendar,
      color: "red",
      description: "Group creation date",
    },
    {
      title: "Group Type",
      value:
        contactCount === 0
          ? "Empty"
          : contactCount < 100
          ? "Small"
          : contactCount < 1000
          ? "Medium"
          : "Large",
      icon: TrendingUp,
      color: "purple",
      description: "Based on contact count",
    },
    {
      title: "Group ID",
      value: group.contact_group_slug,
      icon: FileText,
      color: "orange",
      description: "Unique identifier",
    },
  ];

  const getColorClasses = (color) => {
    switch (color) {
      case "blue":
        return {
          bg: "bg-blue-50",
          border: "border-blue-200",
          iconBg: "bg-blue-100",
          iconColor: "text-blue-600",
          text: "text-blue-800",
          value: "text-blue-900",
        };
      case "red":
        return {
          bg: "bg-red-50",
          border: "border-red-200",
          iconBg: "bg-red-100",
          iconColor: "text-red-600",
          text: "text-red-800",
          value: "text-red-900",
        };
      case "purple":
        return {
          bg: "bg-purple-50",
          border: "border-purple-200",
          iconBg: "bg-purple-100",
          iconColor: "text-purple-600",
          text: "text-purple-800",
          value: "text-purple-900",
        };
      case "orange":
        return {
          bg: "bg-orange-50",
          border: "border-orange-200",
          iconBg: "bg-orange-100",
          iconColor: "text-orange-600",
          text: "text-orange-800",
          value: "text-orange-900",
        };
      default:
        return {
          bg: "bg-gray-50",
          border: "border-gray-200",
          iconBg: "bg-gray-100",
          iconColor: "text-gray-600",
          text: "text-gray-800",
          value: "text-gray-900",
        };
    }
  };

  return (
    <div className="space-y-4">
      {/* Status Banner */}
      {isProcessing && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-yellow-50 rounded-xl border border-yellow-200"
        >
          <div className="flex gap-3 items-center">
            <div className="flex justify-center items-center w-10 h-10 bg-yellow-100 rounded-lg">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-5 h-5"
              >
                <Clock className="w-5 h-5 text-yellow-600" />
              </motion.div>
            </div>
            <div className="flex-1">
              <p className="font-medium text-yellow-800">Processing Contacts</p>
              <p className="text-sm text-yellow-700">
                Your contacts are being processed. This may take a few minutes
                for large files.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {!isProcessing && contactCount > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-green-50 rounded-xl border border-green-200"
        >
          <div className="flex gap-3 items-center">
            <div className="flex justify-center items-center w-10 h-10 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-green-800">Ready to Use</p>
              <p className="text-sm text-green-700">
                Your contact group is ready for SMS campaigns and exports.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {!isProcessing && contactCount === 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-gray-50 rounded-xl border border-gray-200"
        >
          <div className="flex gap-3 items-center">
            <div className="flex justify-center items-center w-10 h-10 bg-gray-100 rounded-lg">
              <AlertCircle className="w-5 h-5 text-gray-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-800">No Contacts</p>
              <p className="text-sm text-gray-700">
                Add contacts to this group to start sending SMS messages.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          const colors = getColorClasses(stat.color);

          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-4 ${colors.bg} rounded-xl border ${colors.border}`}
            >
              <div className="flex gap-3 items-center">
                <div
                  className={`flex justify-center items-center w-10 h-10 ${colors.iconBg} rounded-lg`}
                >
                  <Icon className={`w-5 h-5 ${colors.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${colors.text}`}>
                    {stat.title}
                  </p>
                  <p
                    className={`text-lg font-bold ${colors.value} truncate`}
                    title={stat.value}
                  >
                    {stat.value}
                  </p>
                  <p
                    className="text-xs text-gray-500 truncate"
                    title={stat.description}
                  >
                    {stat.description}
                  </p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Performance Indicator */}
      {contactCount > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200"
        >
          <div className="flex gap-3 items-center">
            <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-lg">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-blue-800">Group Performance</p>
              <p className="text-sm text-blue-700">
                {contactCount < 100
                  ? "Small group - ideal for targeted campaigns"
                  : contactCount < 1000
                  ? "Medium group - good for regional campaigns"
                  : "Large group - perfect for mass messaging"}
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ContactStats;
